/**
 * Notification Settings Form
 *
 * Allows users to configure their notification preferences:
 * - Enable/disable daily digest notifications
 * - Choose digest threshold (overdue only / due today / include upcoming)
 * - Select preferred notification time
 * - Manage notification channels (email, Telegram)
 */
import { useState, useEffect } from 'react';
import {
  Stack,
  Switch,
  Radio,
  Group,
  Button,
  Text,
  Paper,
  Divider,
  Checkbox,
  Skeleton,
  Alert,
  Card,
  Title,
} from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconCheck,
  IconAlertCircle,
  IconMail,
  IconBrandTelegram,
} from '@tabler/icons-react';
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
  useDisconnectTelegram,
  useReEnableChannel,
} from '@/hooks/useNotificationPreferences';
import { TelegramLinkModal } from './TelegramLinkModal';
import type { DigestThreshold } from '@/types/notifications';
import { DIGEST_THRESHOLD_LABELS, TIME_PRESETS } from '@/types/notifications';

export function NotificationSettingsForm() {
  const { data: preferences, isLoading, isError } = useNotificationPreferences();
  const { mutateAsync: updatePreferences, isPending: isSaving } =
    useUpdateNotificationPreferences();
  const { mutateAsync: disconnectTelegram, isPending: isDisconnecting } =
    useDisconnectTelegram();
  const { mutateAsync: reEnableChannel, isPending: isReEnabling } =
    useReEnableChannel();

  // Telegram modal state
  const [telegramModalOpen, setTelegramModalOpen] = useState(false);

  // Form state
  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState<DigestThreshold>('due_today');
  const [preferredTime, setPreferredTime] = useState('08:00:00');
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [telegramEnabled, setTelegramEnabled] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Sync form state with loaded preferences
  useEffect(() => {
    if (preferences) {
      setEnabled(preferences.enabled);
      setThreshold(preferences.digest_threshold);
      setPreferredTime(preferences.preferred_time);
      setEmailEnabled(preferences.email_enabled);
      setTelegramEnabled(preferences.telegram_enabled);
      setHasChanges(false);
    }
  }, [preferences]);

  // Track changes
  useEffect(() => {
    if (preferences) {
      const changed =
        enabled !== preferences.enabled ||
        threshold !== preferences.digest_threshold ||
        preferredTime !== preferences.preferred_time ||
        emailEnabled !== preferences.email_enabled ||
        telegramEnabled !== preferences.telegram_enabled;
      setHasChanges(changed);
    }
  }, [enabled, threshold, preferredTime, emailEnabled, telegramEnabled, preferences]);

  const handleSave = async () => {
    try {
      await updatePreferences({
        enabled,
        digest_threshold: threshold,
        preferred_time: preferredTime,
        email_enabled: emailEnabled,
        telegram_enabled: telegramEnabled,
        setup_completed: true,
      });
      notifications.show({
        title: 'Settings saved',
        message: 'Your notification preferences have been updated.',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to save notification preferences.',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    }
  };

  const handleDisconnectTelegram = async () => {
    try {
      await disconnectTelegram();
      notifications.show({
        title: 'Disconnected',
        message: 'Telegram has been disconnected from notifications.',
        color: 'blue',
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to disconnect Telegram. Please try again.',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    }
  };

  const handleReEnableEmail = async () => {
    try {
      await reEnableChannel('email');
      notifications.show({
        title: 'Email re-enabled',
        message: 'Email notifications have been re-enabled.',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to re-enable email notifications.',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    }
  };

  const handleTimePreset = (time: string) => {
    setPreferredTime(time);
  };

  const isTimePreset = (time: string) => preferredTime === time;

  if (isLoading) {
    return (
      <Card withBorder p="lg">
        <Stack gap="md">
          <Skeleton height={40} />
          <Skeleton height={100} />
          <Skeleton height={60} />
          <Skeleton height={80} />
        </Stack>
      </Card>
    );
  }

  if (isError) {
    return (
      <Alert color="red" icon={<IconAlertCircle />} title="Error">
        Failed to load notification preferences. Please try again.
      </Alert>
    );
  }

  return (
    <Card withBorder p="lg">
      <Stack gap="lg">
        <div>
          <Title order={4}>Task Reminders</Title>
          <Text size="sm" c="dimmed">
            Configure when and how you receive task notifications.
          </Text>
        </div>

        {/* Main toggle */}
        <Paper p="md" withBorder>
          <Group justify="space-between">
            <div>
              <Text fw={500}>Enable daily digest</Text>
              <Text size="sm" c="dimmed">
                Receive a daily summary of tasks that need your attention
              </Text>
            </div>
            <Switch
              checked={enabled}
              onChange={(e) => setEnabled(e.currentTarget.checked)}
              aria-label="Enable daily digest"
              size="lg"
            />
          </Group>
        </Paper>

        {/* Threshold selection */}
        <Paper p="md" withBorder style={{ opacity: enabled ? 1 : 0.5 }}>
          <Stack gap="sm">
            <Text fw={500}>When to notify</Text>
            <Radio.Group
              value={threshold}
              onChange={(v) => setThreshold(v as DigestThreshold)}
            >
              <Stack gap="xs">
                <Radio
                  value="overdue_only"
                  label={DIGEST_THRESHOLD_LABELS.overdue_only}
                  disabled={!enabled}
                />
                <Radio
                  value="due_today"
                  label={DIGEST_THRESHOLD_LABELS.due_today}
                  disabled={!enabled}
                />
                <Radio
                  value="include_upcoming"
                  label={DIGEST_THRESHOLD_LABELS.include_upcoming}
                  disabled={!enabled}
                />
              </Stack>
            </Radio.Group>
          </Stack>
        </Paper>

        {/* Time selection */}
        <Paper p="md" withBorder style={{ opacity: enabled ? 1 : 0.5 }}>
          <Stack gap="sm">
            <Text fw={500}>Preferred time</Text>
            <Group gap="xs">
              {TIME_PRESETS.map((preset) => (
                <Button
                  key={preset.value}
                  variant={isTimePreset(preset.value) ? 'filled' : 'outline'}
                  size="sm"
                  onClick={() => handleTimePreset(preset.value)}
                  disabled={!enabled}
                  data-active={isTimePreset(preset.value)}
                >
                  {preset.label}
                </Button>
              ))}
              <TimeInput
                value={preferredTime.slice(0, 5)}
                onChange={(e) =>
                  setPreferredTime(e.currentTarget.value + ':00')
                }
                disabled={!enabled}
                w={100}
              />
            </Group>
          </Stack>
        </Paper>

        <Divider label="Channels" labelPosition="left" />

        {/* Channels */}
        <Paper p="md" withBorder>
          <Stack gap="md">
            {/* Email - always enabled, shows user's email */}
            <Group justify="space-between">
              <Group gap="sm">
                <IconMail size={20} />
                <div>
                  <Text fw={500}>Email</Text>
                  <Text size="sm" c="dimmed">
                    Notifications sent to your account email
                  </Text>
                </div>
              </Group>
              <Checkbox
                checked={emailEnabled}
                onChange={(e) => setEmailEnabled(e.currentTarget.checked)}
                disabled={!!preferences?.email_error_message && !preferences.email_enabled}
                aria-label="Email notifications"
              />
            </Group>

            {preferences?.email_error_message && !preferences.email_enabled && (
              <Alert color="orange" variant="light" mt="xs" p="xs">
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  <div>
                    <Text size="xs">
                      Delivery failed: {preferences.email_error_message}
                    </Text>
                    <Text size="xs" c="dimmed" mt={4}>
                      Check your email address, then re-enable notifications.
                    </Text>
                  </div>
                  <Button
                    size="xs"
                    variant="subtle"
                    color="orange"
                    onClick={handleReEnableEmail}
                    loading={isReEnabling}
                  >
                    Re-enable
                  </Button>
                </Group>
              </Alert>
            )}

            <Divider />

            {/* Telegram */}
            <Group justify="space-between">
              <Group gap="sm">
                <IconBrandTelegram size={20} />
                <div>
                  <Text fw={500}>Telegram</Text>
                  {preferences?.telegram_chat_id ? (
                    <Text size="sm" c="green">
                      Connected
                    </Text>
                  ) : (
                    <Text size="sm" c="dimmed">
                      Get notifications via Telegram
                    </Text>
                  )}
                </div>
              </Group>
              {preferences?.telegram_chat_id ? (
                <Button
                  variant="outline"
                  color="red"
                  size="xs"
                  onClick={handleDisconnectTelegram}
                  loading={isDisconnecting}
                >
                  Disconnect
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="xs"
                  leftSection={<IconBrandTelegram size={16} />}
                  onClick={() => {
                    if (preferences?.telegram_error_message) {
                      reEnableChannel('telegram');
                    }
                    setTelegramModalOpen(true);
                  }}
                >
                  Connect Telegram
                </Button>
              )}
            </Group>

            {preferences?.telegram_error_message && !preferences.telegram_enabled && (
              <Alert color="orange" variant="light" mt="xs" p="xs">
                <Text size="xs">
                  {preferences.telegram_error_message.includes('blocked')
                    ? "You blocked the bot. Click 'Connect Telegram' to reconnect."
                    : `Error: ${preferences.telegram_error_message}`
                  }
                </Text>
              </Alert>
            )}

            {/* Telegram Link Modal */}
            <TelegramLinkModal
              opened={telegramModalOpen}
              onClose={() => setTelegramModalOpen(false)}
            />
          </Stack>
        </Paper>

        {/* Save button */}
        <Group justify="flex-end">
          <Button
            onClick={handleSave}
            loading={isSaving}
            disabled={!hasChanges}
            leftSection={<IconCheck size={16} />}
          >
            Save changes
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
